import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  MapPin, Smartphone, Plus, Trash2, Eye, Loader2, Footprints, Target, Download, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { GeofenceMap } from "../components/GeofenceMap";

export default function FootfallPage() {
  const { api, isAdmin } = useAuth();
  const [tab, setTab] = useState("device");
  const [lists, setLists] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [locationGroups, setLocationGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState("");
  const [deviceReport, setDeviceReport] = useState(null);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceIdLocations, setDeviceIdLocations] = useState([]);
  const [selectedFence, setSelectedFence] = useState("");
  const [geoReport, setGeoReport] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [deviceLocations, setDeviceLocations] = useState([]);
  const [period, setPeriod] = useState("7d");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [showCreateFence, setShowCreateFence] = useState(false);
  const [newFence, setNewFence] = useState({ name: "", lat: "", lon: "", radius_m: "500", description: "", polygon: null });
  const [creating, setCreating] = useState(false);
  const [deleteFenceId, setDeleteFenceId] = useState(null);
  const [geoDeviceList, setGeoDeviceList] = useState("");
  const [geoDeviceMatch, setGeoDeviceMatch] = useState(null);
  const [geoDeviceLoading, setGeoDeviceLoading] = useState(false);
  const [allDeviceLists, setAllDeviceLists] = useState([]);
  const [showUploadGroup, setShowUploadGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", file: null });
  const [uploadingGroup, setUploadingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupReport, setGroupReport] = useState(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupRadius, setGroupRadius] = useState("200");
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [groupDeviceList, setGroupDeviceList] = useState("");
  const [groupDeviceMatch, setGroupDeviceMatch] = useState(null);
  const [groupDeviceLocations, setGroupDeviceLocations] = useState([]);
  const [groupAllDeviceLocations, setGroupAllDeviceLocations] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/lists/for-select/all").then(r => {
        setLists(r.data.filter(l => l.category === "IFA Device IDs"));
        setAllDeviceLists(r.data.filter(l => l.category === "IFA Device IDs"));
      }).catch(() => {}),
      api.get("/footfall/geofences").then(r => setGeofences(r.data)).catch(() => {}),
      api.get("/footfall/location-groups").then(r => setLocationGroups(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [api]);

  const bp = () => period === "custom" && customRange.start && customRange.end
    ? `period=custom&start_date=${customRange.start}&end_date=${customRange.end}` : `period=${period}`;

  const [deviceLocLoading, setDeviceLocLoading] = useState(false);

  const fetchDeviceReport = async () => {
    if (!selectedList) return toast.error("Select a device ID list");
    setDeviceLoading(true);
    setDeviceIdLocations([]);
    setDeviceReport(null);
    try {
      const r = await api.get(`/footfall/report/device-ids?list_id=${selectedList}&${bp()}`);
      setDeviceReport(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Report generation failed"); }
    finally { setDeviceLoading(false); }
  };

  const fetchDeviceLocations = async () => {
    if (!selectedList) return;
    setDeviceLocLoading(true);
    try {
      const locs = await api.get(`/footfall/report/device-ids/locations?list_id=${selectedList}&${bp()}`);
      setDeviceIdLocations(locs.data.devices || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load locations"); }
    finally { setDeviceLocLoading(false); }
  };

  const fetchGeoReport = async () => {
    if (!selectedFence) return toast.error("Select a geofence");
    setGeoLoading(true);
    setDeviceLocations([]);
    setGeoDeviceMatch(null);
    try {
      const [rpt, devs] = await Promise.all([
        api.get(`/footfall/report/geofence?fence_id=${selectedFence}&${bp()}`),
        api.get(`/footfall/report/geofence/devices?fence_id=${selectedFence}&${bp()}`),
      ]);
      setGeoReport(rpt.data);
      setDeviceLocations(devs.data.devices || []);

      // If device list selected, also run cross-match
      if (geoDeviceList) {
        const match = await api.get(`/footfall/report/geofence-device-match?fence_id=${selectedFence}&list_id=${geoDeviceList}&${bp()}`);
        setGeoDeviceMatch(match.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setGeoLoading(false); }
  };

  const fetchGroupReport = async () => {
    if (!selectedGroup) return toast.error("Select a location group");
    setGroupLoading(true);
    setGroupDeviceMatch(null);
    setGroupDeviceLocations([]);
    setGroupAllDeviceLocations([]);
    try {
      const r = await api.get(`/footfall/report/location-group?group_id=${selectedGroup}&radius_m=${groupRadius}&${bp()}`);
      setGroupReport(r.data);
      setGroupAllDeviceLocations(r.data.device_locations || []);
      if (groupDeviceList) {
        const m = await api.get(`/footfall/report/location-group-device-match?group_id=${selectedGroup}&list_id=${groupDeviceList}&radius_m=${groupRadius}&${bp()}`);
        setGroupDeviceMatch(m.data);
        setGroupDeviceLocations(m.data.device_locations || []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setGroupLoading(false); }
  };

  const createGeofence = async () => {
    if (!newFence.name || !newFence.lat || !newFence.lon) return toast.error("Name, lat, lon required");
    setCreating(true);
    try {
      const payload = { name: newFence.name, lat: parseFloat(newFence.lat), lon: parseFloat(newFence.lon), radius_m: parseFloat(newFence.radius_m) || 500, description: newFence.description };
      if (newFence.polygon) payload.polygon = newFence.polygon;
      const r = await api.post("/footfall/geofences", payload);
      setGeofences([...geofences, r.data]);
      setShowCreateFence(false);
      setNewFence({ name: "", lat: "", lon: "", radius_m: "500", description: "", polygon: null });
      toast.success("Geofence created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const uploadLocationGroup = async () => {
    if (!newGroup.name || !newGroup.file) return toast.error("Name and CSV file required");
    setUploadingGroup(true);
    try {
      const fd = new FormData();
      fd.append("name", newGroup.name);
      fd.append("file", newGroup.file);
      const r = await api.post("/footfall/location-groups", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLocationGroups([...locationGroups, r.data]);
      setShowUploadGroup(false);
      setNewGroup({ name: "", file: null });
      toast.success(`Uploaded ${r.data.location_count} locations`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUploadingGroup(false); }
  };

  const downloadDeviceIds = (devices, filename) => {
    const csv = "device_id\n" + devices.map(d => d.device_id).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    toast.success("Downloaded");
  };

  const f = (n) => n == null ? "0" : Number(n).toLocaleString();

  return (
    <div className="space-y-5" data-testid="footfall-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Footprints className="h-7 w-7 text-accent" /> Footfall</h1>
          <p className="text-muted-foreground text-sm">Device ID matching, geofence & location group reports</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="today">Today</SelectItem><SelectItem value="yesterday">Yesterday</SelectItem><SelectItem value="7d">Last 7 Days</SelectItem><SelectItem value="30d">Last 30 Days</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select>
        {period === "custom" && (<>
          <Input type="date" value={customRange.start} onChange={e => setCustomRange({...customRange, start: e.target.value})} className="w-[140px]" />
          <span className="text-sm text-muted-foreground">to</span>
          <Input type="date" value={customRange.end} min={customRange.start} onChange={e => setCustomRange({...customRange, end: e.target.value})} className="w-[140px]" />
        </>)}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="device" className="gap-2"><Smartphone className="h-4 w-4" /> Device IDs</TabsTrigger>
          <TabsTrigger value="geofence" className="gap-2"><Target className="h-4 w-4" /> Geofence</TabsTrigger>
          <TabsTrigger value="locations" className="gap-2"><MapPin className="h-4 w-4" /> Location Groups</TabsTrigger>
        </TabsList>

        {/* DEVICE ID TAB */}
        <TabsContent value="device" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Device ID Footfall Report</CardTitle><CardDescription>Match bid requests against an uploaded IFA Device ID list</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Select value={selectedList} onValueChange={setSelectedList}><SelectTrigger className="w-[300px]"><SelectValue placeholder="Select Device ID list" /></SelectTrigger>
                  <SelectContent>{lists.map(l => <SelectItem key={l.id} value={l.id}>{l.name} ({l.item_count})</SelectItem>)}{lists.length === 0 && <SelectItem value="none" disabled>Upload IFA list in List Manager</SelectItem>}</SelectContent></Select>
                <Button onClick={fetchDeviceReport} disabled={deviceLoading || !selectedList}>{deviceLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Generate</Button>
                {deviceReport?.by_device?.length > 0 && <Button variant="outline" size="sm" onClick={() => downloadDeviceIds(deviceReport.by_device, `matched_devices_${selectedList}.csv`)}><Download className="h-4 w-4 mr-1" />Download Matched IDs</Button>}
              </div>
              {deviceReport && (<div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MC l="Total Matched" v={f(deviceReport.total_matched)} c="text-blue-600" />
                  <MC l="Unique Devices" v={f(deviceReport.unique_devices)} c="text-violet-600" />
                  <MC l="Impressions" v={f(deviceReport.total_impressions)} c="text-emerald-600" />
                  <MC l="In List" v={f(deviceReport.device_count_in_list)} c="text-muted-foreground" />
                </div>
                <div className="flex items-center gap-2">
                  {deviceIdLocations.length === 0 && (
                    <Button variant="outline" size="sm" onClick={fetchDeviceLocations} disabled={deviceLocLoading} data-testid="view-map-btn">
                      {deviceLocLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MapPin className="h-4 w-4 mr-1" />}
                      {deviceLocLoading ? "Loading Map..." : "View on Map"}
                    </Button>
                  )}
                </div>
                {deviceIdLocations.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Matched Device Locations ({deviceIdLocations.length})</CardTitle></CardHeader>
                    <CardContent>
                      <GeofenceMap geofences={[]} deviceLocations={deviceIdLocations} height="350px" />
                    </CardContent>
                  </Card>
                )}
                {deviceReport.by_date?.length > 0 && <TBL title="By Date" heads={["Date","Matched","Unique","Impressions"]} rows={deviceReport.by_date.map(r => [r.date, f(r.matched), f(r.unique_devices), f(r.impressions)])} />}
                {deviceReport.by_os?.length > 0 && <TBL title="By OS" heads={["OS","Matched","Unique"]} rows={deviceReport.by_os.map(r => [r.os||"Unknown", f(r.matched), f(r.unique_devices)])} />}
              </div>)}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GEOFENCE TAB */}
        <TabsContent value="geofence" className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Geofence Map</CardTitle></CardHeader>
            <CardContent>
              <GeofenceMap geofences={geofences} selectedFence={geofences.find(g => g.id === selectedFence)} deviceLocations={deviceLocations} height="400px" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><div className="flex items-center justify-between"><div><CardTitle>Geofences</CardTitle></div>
              {isAdmin && <Button size="sm" onClick={() => setShowCreateFence(true)}><Plus className="h-4 w-4 mr-1" /> Create</Button>}</div></CardHeader>
            <CardContent>
              {geofences.length > 0 ? (<Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Lat</TableHead><TableHead>Lon</TableHead><TableHead>Radius</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader><TableBody>
                {geofences.map(g => (<TableRow key={g.id}><TableCell className="font-medium"><MapPin className="h-3.5 w-3.5 inline mr-1 text-red-500" />{g.name}</TableCell><TableCell className="font-mono text-sm">{g.lat}</TableCell><TableCell className="font-mono text-sm">{g.lon}</TableCell><TableCell className="font-mono text-sm">{g.radius_m}m</TableCell><TableCell className="text-xs">{g.polygon?.length >= 3 ? "Polygon" : "Circle"}</TableCell><TableCell className="text-right">{isAdmin && <Button size="sm" variant="ghost" onClick={() => setDeleteFenceId(g.id)}><Trash2 className="h-4 w-4" /></Button>}</TableCell></TableRow>))}
              </TableBody></Table>) : <p className="text-center py-6 text-muted-foreground">No geofences</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Geofence Footfall Report</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={selectedFence} onValueChange={setSelectedFence}><SelectTrigger className="w-[250px]"><SelectValue placeholder="Select geofence" /></SelectTrigger>
                  <SelectContent>{geofences.map(g => <SelectItem key={g.id} value={g.id}>{g.name} ({g.radius_m}m)</SelectItem>)}</SelectContent></Select>
                <Select value={geoDeviceList || "none"} onValueChange={v => setGeoDeviceList(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-[250px]"><SelectValue placeholder="Device ID list (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No device list filter</SelectItem>
                    {allDeviceLists.map(l => <SelectItem key={l.id} value={l.id}>{l.name} ({l.item_count})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={fetchGeoReport} disabled={geoLoading || !selectedFence}>{geoLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Generate</Button>
                {deviceLocations.length > 0 && <Button variant="outline" size="sm" onClick={() => downloadDeviceIds(deviceLocations, `geofence_devices_${selectedFence}.csv`)}><Download className="h-4 w-4 mr-1" />Download IDs</Button>}
              </div>
              {geoReport && (<div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MC l="Total Matched" v={f(geoReport.total_matched)} c="text-blue-600" />
                  <MC l="Unique Devices" v={f(geoReport.unique_devices)} c="text-violet-600" />
                  <MC l="Impressions" v={f(geoReport.total_impressions)} c="text-emerald-600" />
                </div>

                {/* Cross-match: Device list within geofence */}
                {geoDeviceMatch && (
                  <Card className="border-violet-200 dark:border-violet-900">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Device List Match: {geoDeviceMatch.list_name}</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <MC l="Devices in List" v={f(geoDeviceMatch.total_in_list)} c="text-muted-foreground" />
                        <MC l="Found in Geofence" v={f(geoDeviceMatch.matched_devices)} c="text-violet-600" />
                        <MC l="Match Rate" v={`${geoDeviceMatch.match_rate}%`} c="text-emerald-600" />
                        <MC l="Total Visits" v={f(geoDeviceMatch.devices?.reduce((s, d) => s + d.count, 0))} c="text-blue-600" />
                      </div>
                      {geoDeviceMatch.devices?.length > 0 && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => downloadDeviceIds(geoDeviceMatch.devices, `matched_in_geofence_${selectedFence}.csv`)}><Download className="h-4 w-4 mr-1" />Download Matched IDs</Button>
                          <Table><TableHeader><TableRow><TableHead>Device ID</TableHead><TableHead>OS</TableHead><TableHead className="text-right">Visits</TableHead></TableRow></TableHeader><TableBody>
                            {geoDeviceMatch.devices.slice(0, 20).map((d, i) => (
                              <TableRow key={i}><TableCell className="font-mono text-xs max-w-[250px] truncate">{d.device_id}</TableCell><TableCell className="text-sm">{d.os || "—"}</TableCell><TableCell className="text-right font-mono">{f(d.count)}</TableCell></TableRow>
                            ))}
                          </TableBody></Table>
                        </>
                      )}
                      {geoDeviceMatch.matched_devices === 0 && <p className="text-sm text-muted-foreground text-center py-3">No devices from the list were found in this geofence</p>}
                    </CardContent>
                  </Card>
                )}

                {geoReport.by_date?.length > 0 && <TBL title="By Date" heads={["Date","Matched","Unique","Impressions"]} rows={geoReport.by_date.map(r => [r.date, f(r.matched), f(r.unique_devices), f(r.impressions)])} />}
                {geoReport.by_os?.length > 0 && <TBL title="By OS" heads={["OS","Matched","Unique"]} rows={geoReport.by_os.map(r => [r.os, f(r.matched), f(r.unique_devices)])} />}
              </div>)}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOCATION GROUPS TAB */}
        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader><div className="flex items-center justify-between"><div><CardTitle>Location Groups</CardTitle><CardDescription>Upload CSV of locations (e.g. all 7-Eleven stores) and see device footfall per location</CardDescription></div>
              {isAdmin && <Button size="sm" onClick={() => setShowUploadGroup(true)}><Upload className="h-4 w-4 mr-1" /> Upload CSV</Button>}</div></CardHeader>
            <CardContent>
              {locationGroups.length > 0 ? (<Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead className="text-right">Locations</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader><TableBody>
                {locationGroups.map(g => (<TableRow key={g.id}><TableCell className="font-medium">{g.name}</TableCell><TableCell className="text-right font-mono">{g.location_count}</TableCell><TableCell className="text-sm text-muted-foreground">{g.created_at?.slice(0,10)}</TableCell><TableCell className="text-right">{isAdmin && <Button size="sm" variant="ghost" onClick={() => setDeleteGroupId(g.id)}><Trash2 className="h-4 w-4" /></Button>}</TableCell></TableRow>))}
              </TableBody></Table>) : <p className="text-center py-6 text-muted-foreground">No location groups. Upload a CSV with name, lat, lon columns.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Location Group Footfall Report</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={selectedGroup} onValueChange={setSelectedGroup}><SelectTrigger className="w-[220px]"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>{locationGroups.map(g => <SelectItem key={g.id} value={g.id}>{g.name} ({g.location_count})</SelectItem>)}</SelectContent></Select>
                <Select value={groupDeviceList || "none"} onValueChange={v => setGroupDeviceList(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-[220px]"><SelectValue placeholder="Device list (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All devices (no filter)</SelectItem>
                    {allDeviceLists.map(l => <SelectItem key={l.id} value={l.id}>{l.name} ({l.item_count})</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1"><Label className="text-xs whitespace-nowrap">Radius (m):</Label><Input type="number" value={groupRadius} onChange={e => setGroupRadius(e.target.value)} className="w-[80px]" /></div>
                <Button onClick={fetchGroupReport} disabled={groupLoading || !selectedGroup}>{groupLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Generate</Button>
              </div>

              {/* Device List Cross-Match Results */}
              {groupDeviceMatch && (
                <Card className="border-violet-200 dark:border-violet-900">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Device List Match: {groupDeviceMatch.list_name}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <MC l="Devices in List" v={f(groupDeviceMatch.total_in_list)} c="text-muted-foreground" />
                      <MC l="Found at Locations" v={f(groupDeviceMatch.matched_devices)} c="text-violet-600" />
                      <MC l="Match Rate" v={`${groupDeviceMatch.match_rate}%`} c="text-emerald-600" />
                      <MC l="Locations with Matches" v={f(groupDeviceMatch.locations?.filter(l => l.matched_devices > 0).length)} c="text-blue-600" />
                    </div>
                    <GeofenceMap
                      geofences={[]}
                      locationGroups={[{ id: "match", name: groupDeviceMatch.group_name, locations: groupDeviceMatch.locations }]}
                      deviceLocations={groupDeviceLocations}
                      locationRadius={parseFloat(groupRadius) || 200}
                      height="350px"
                    />
                    {groupDeviceMatch.matched_devices > 0 && (
                      <Button variant="outline" size="sm" onClick={() => {
                        const ids = groupDeviceMatch.locations.flatMap(l => Array(l.matched_devices).fill(l.name));
                        const csv = "location,matched_devices,visits\n" + groupDeviceMatch.locations.filter(l => l.matched_devices > 0).map(l => `"${l.name}",${l.matched_devices},${l.visits}`).join("\n");
                        const blob = new Blob([csv], { type: "text/csv" });
                        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `location_match_${selectedGroup}.csv`; a.click();
                        toast.success("Downloaded");
                      }}><Download className="h-4 w-4 mr-1" />Download Report</Button>
                    )}
                    <Table><TableHeader><TableRow><TableHead>Location</TableHead><TableHead>Lat</TableHead><TableHead>Lon</TableHead><TableHead className="text-right">Matched Devices</TableHead><TableHead className="text-right">Visits</TableHead></TableRow></TableHeader><TableBody>
                      {(groupDeviceMatch.locations || []).slice(0, 50).map((l, i) => (
                        <TableRow key={i} className={l.matched_devices > 0 ? "" : "opacity-50"}>
                          <TableCell className="font-medium">{l.name || `Location ${i+1}`}</TableCell>
                          <TableCell className="font-mono text-xs">{l.lat}</TableCell>
                          <TableCell className="font-mono text-xs">{l.lon}</TableCell>
                          <TableCell className={`text-right font-mono ${l.matched_devices > 0 ? "text-violet-600 font-semibold" : ""}`}>{f(l.matched_devices)}</TableCell>
                          <TableCell className="text-right font-mono">{f(l.visits)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table>
                  </CardContent>
                </Card>
              )}

              {/* Standard Group Report (all devices) */}
              {groupReport && !groupDeviceMatch && (<div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MC l="Total Matched" v={f(groupReport.total_matched)} c="text-blue-600" />
                  <MC l="Unique Devices" v={f(groupReport.unique_devices)} c="text-violet-600" />
                  <MC l="Locations" v={f(groupReport.location_count)} c="text-muted-foreground" />
                </div>
                <GeofenceMap
                  geofences={[]}
                  locationGroups={[{ id: "report", name: groupReport.group_name, locations: groupReport.locations }]}
                  deviceLocations={groupAllDeviceLocations}
                  locationRadius={parseFloat(groupRadius) || 200}
                  height="400px"
                />
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Per Location ({groupReport.locations?.length})</CardTitle></CardHeader><CardContent>
                  <Table><TableHeader><TableRow><TableHead>Location</TableHead><TableHead>Lat</TableHead><TableHead>Lon</TableHead><TableHead className="text-right">Matched</TableHead><TableHead className="text-right">Unique Devices</TableHead></TableRow></TableHeader><TableBody>
                    {(groupReport.locations || []).slice(0, 50).map((l, i) => (<TableRow key={i}><TableCell className="font-medium">{l.name || `Location ${i+1}`}</TableCell><TableCell className="font-mono text-xs">{l.lat}</TableCell><TableCell className="font-mono text-xs">{l.lon}</TableCell><TableCell className="text-right font-mono">{f(l.count)}</TableCell><TableCell className="text-right font-mono text-violet-600">{f(l.unique_devices)}</TableCell></TableRow>))}
                  </TableBody></Table></CardContent></Card>
              </div>)}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Geofence */}
      <Dialog open={showCreateFence} onOpenChange={setShowCreateFence}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Geofence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Click map to set center, or draw a polygon using the draw tools.</p>
            <GeofenceMap
              geofences={[]}
              onMapClick={(ll) => setNewFence({...newFence, lat: ll.lat.toFixed(6), lon: ll.lng.toFixed(6)})}
              clickMarker={newFence.lat && newFence.lon ? {lat: parseFloat(newFence.lat), lon: parseFloat(newFence.lon)} : null}
              enablePolygonDraw={true}
              onPolygonComplete={(poly) => setNewFence({...newFence, polygon: poly})}
              height="250px"
            />
            {newFence.polygon && <p className="text-xs text-emerald-600">Polygon drawn with {newFence.polygon.length} points</p>}
            <div className="space-y-2"><Label>Name *</Label><Input value={newFence.name} onChange={e => setNewFence({...newFence, name: e.target.value})} placeholder="e.g. Times Square" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs">Latitude *</Label><Input type="number" step="any" value={newFence.lat} onChange={e => setNewFence({...newFence, lat: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Longitude *</Label><Input type="number" step="any" value={newFence.lon} onChange={e => setNewFence({...newFence, lon: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Radius (m)</Label><Input type="number" value={newFence.radius_m} onChange={e => setNewFence({...newFence, radius_m: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={newFence.description} onChange={e => setNewFence({...newFence, description: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateFence(false)}>Cancel</Button><Button onClick={createGeofence} disabled={creating}>{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MapPin className="h-4 w-4 mr-1" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Location Group */}
      <Dialog open={showUploadGroup} onOpenChange={setShowUploadGroup}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload Location Group</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Group Name *</Label><Input value={newGroup.name} onChange={e => setNewGroup({...newGroup, name: e.target.value})} placeholder="e.g. 7-Eleven Malaysia" /></div>
            <div className="space-y-2"><Label>CSV File *</Label><Input type="file" accept=".csv" onChange={e => setNewGroup({...newGroup, file: e.target.files[0]})} /><p className="text-xs text-muted-foreground">CSV with columns: name, lat, lon (or latitude, longitude)</p></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowUploadGroup(false)}>Cancel</Button><Button onClick={uploadLocationGroup} disabled={uploadingGroup}>{uploadingGroup ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}Upload</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialogs */}
      <AlertDialog open={!!deleteFenceId} onOpenChange={() => setDeleteFenceId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Geofence?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={async () => { await api.delete(`/footfall/geofences/${deleteFenceId}`); setGeofences(g => g.filter(x => x.id !== deleteFenceId)); setDeleteFenceId(null); toast.success("Deleted"); }} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
      <AlertDialog open={!!deleteGroupId} onOpenChange={() => setDeleteGroupId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Location Group?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={async () => { await api.delete(`/footfall/location-groups/${deleteGroupId}`); setLocationGroups(g => g.filter(x => x.id !== deleteGroupId)); setDeleteGroupId(null); toast.success("Deleted"); }} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
}

function MC({ l, v, c }) { return <Card className="p-3"><p className="text-[10px] text-muted-foreground uppercase">{l}</p><p className={`text-xl font-bold mt-0.5 ${c}`}>{v}</p></Card>; }
function TBL({ title, heads, rows }) {
  return <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader><CardContent><Table><TableHeader><TableRow>{heads.map((h,i) => <TableHead key={i} className={i > 0 ? "text-right" : ""}>{h}</TableHead>)}</TableRow></TableHeader><TableBody>{rows.map((r,i) => <TableRow key={i}>{r.map((c,j) => <TableCell key={j} className={`font-mono ${j > 0 ? "text-right" : ""}`}>{c}</TableCell>)}</TableRow>)}</TableBody></Table></CardContent></Card>;
}
